/* eslint-disable react-hooks/set-state-in-effect -- this selector intentionally syncs local state from effect-driven external changes. */
import {useCallback, useEffect, useReducer, useRef, useState} from "react";
import {Observer} from "mobx-react";
import styled from "styled-components";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {View} from "../../../data/models/View";
import {useClickOutside, useCloseOnKeyboardPress} from "../utils";
import {notify} from "../../../utils/Notify";
import {useAppStore} from "../../../StateManager";
import type {IViewNotificationStatus} from "../../../StateManager";
import type {INotificationProps} from "../popup/NotificationV5";
import {NotificationType} from "../popup/NotificationV5";
import {baseDistance, ELLIPSIS, FlexCenter, FLEXCENTER, fontSize, radius, zIndex} from "../styles/styles";
import {colorPalette} from "../styles/colorPalette";
import {ConfirmWindowV5} from "../popup/ConfirmWindowV5";
import {Permission, XyiconFeature} from "../../../generated/api/base";
import GlobeIcon from "../icons/globe.svg?react";
import UserIcon from "../icons/user.svg?react";
import UsersIcon from "../icons/users.svg?react";
import ChevronDownIcon from "../icons/chevron-down.svg?react";
import CirclePlusIcon from "../icons/circle-plus.svg?react";
import DotsHorizontalIcon from "../icons/dots-horizontal.svg?react";
import DescriptionIcon from "../icons/file-pencil-alt.svg?react";
import ShareIcon from "../icons/share.svg?react";
import RenameIcon from "../icons/pen-field.svg?react";
import DuplicateIcon from "../icons/clone.svg?react";
import SwitchIcon from "../icons/switch-horizontal.svg?react";
import DeleteIcon from "../icons/trash.svg?react";
import {TextInputV5} from "../details/datatypes/TextInputV5";
import {DropdownButtonStyled, DropdownButtonV5} from "../interaction/DropdownButtonV5";
import {SearchFieldV5} from "../input/search/SearchFieldV5";
import {IconButtonV5} from "../interaction/IconButtonV5";
import {ViewSharingPopup} from "../sharing/ViewSharingPopup";
import {ReactUtils} from "../../utils/ReactUtils";
import {SearchFieldStyled} from "../input/search/SearchField.styled";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import {PopupV5} from "../popup/PopupV5";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import {ViewTooltipStyled} from "../navigation/views/ViewItemV5";
import type {TransformObj} from "../../../utils/dom/DomUtils";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../utils/dom/DomUtils";
import type {IDropdownOption, ISubOptionWrapper} from "../interaction/DropdownOptionsV5";
import type {Space} from "../../../data/models/Space";
import type {ModuleViewV5} from "../abstract/ModuleViewV5";

interface IViewSelectorV5Props {
	readonly feature: XyiconFeature;
}

export const ViewSelectorV5 = (props: IViewSelectorV5Props) => {
	const {feature} = props;
	const appState = useAppStore((state) => state.appState);
	const setViewNotificationStatus = useAppStore((state) => state.setViewNotificationStatus);
	const viewNotificationStatus = useAppStore((state) => state.viewNotificationStatus);
	const [search, setSearch] = useState<string>("");
	const selectedViewRef = useRef<HTMLDivElement>(null);
	const viewPopupRef = useRef<HTMLDivElement>(null);
	const viewInNameEditModeRef = useRef<HTMLDivElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const [isOpenSelectView, setIsOpenSelectView] = useState<boolean>(false);
	const [viewInNameEditMode, setViewInNameEditMode] = useState<View | null>(null);
	const [viewItemForSharePanel, setViewItemForSharePanel] = useState<View | null>(null);
	const [editedViewId, setEditedViewId] = useState<string>("");
	const [descriptionValue, setDescriptionValue] = useState<string>("");
	const [descriptionTooltipTransform, setDescriptionTooltipTransform] = useState<TransformObj>(null);
	const [hoveredDescriptionId, setHoveredDescriptionId] = useState<string>("");
	const [isDescriptionSaving, setIsDescriptionSaving] = useState<boolean>(false);
	const [, forceUpdate] = useReducer((x) => x + 1, 0);
	const viewRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
	const _timeOutId = useRef<number>(-1);

	const {actions} = appState;
	const viewService = appState.app.transport.services.view;
	const views = actions.getViews(feature).toSorted((a: View, b: View) => StringUtils.sortIgnoreCase(a.name, b.name));
	const isAnyTypeNotHidden = actions.hasAnyTypeTheGivenPermissionInModule(feature, Permission.Update);
	const isLoadingList = (appState.app.moduleViews[feature] as ModuleViewV5)?.isLoadingList;
	const shouldShowNoSpacesNotification = feature === XyiconFeature.Space && !isLoadingList;

	const getEditedViewMaybe = () => {
		if (editedViewId) {
			return actions.getViewById(editedViewId);
		}

		return null;
	};

	const editedViewMaybe = getEditedViewMaybe();
	const {notificationContainer} = appState.app;

	const onChangeView = useCallback(
		(view: View) => {
			actions.selectView(view);

			if (viewInNameEditMode) {
				setViewInNameEditMode(null);
			}
		},
		[actions, viewInNameEditMode],
	);

	const onClose = useCallback(() => {
		setIsOpenSelectView(false);

		selectedViewRef?.current.classList.remove("hover");
	}, []);

	useClickOutside([selectedViewRef, viewPopupRef], onClose);

	useClickOutside([viewInNameEditModeRef], () => {
		setViewInNameEditMode(null);
	});

	useCloseOnKeyboardPress(onClose, KeyboardListener.KEY_ESCAPE);

	const onDuplicateClick = async (view: View) => {
		if (view && !viewInNameEditMode) {
			const duplicate = await view.duplicate();

			if (duplicate) {
				setViewInNameEditMode(duplicate);
			}
		}
	};

	const onDeleteClick = async (view: View) => {
		const confirmed = await ConfirmWindowV5.open("Are you sure you want to delete the selected 1 item?");

		if (confirmed) {
			await appState.app.transport.services.view.delete(view.id);
			forceUpdate();
		}
	};

	const onCloseSharePanel = () => {
		setViewItemForSharePanel(null);
	};

	const onRenameClick = (view: View) => {
		setViewInNameEditMode(view);
	};

	const onMarkAsGlobalClick = async (view: View) => {
		await appState.app.transport.services.view.markViewAsGlobal(view);
		forceUpdate();
	};

	const onMarkAsPrivateClick = async (view: View) => {
		await appState.app.transport.services.view.markViewAsPrivate(view);
		forceUpdate();
	};

	const onEditDescriptionInput = (value: string) => {
		setDescriptionValue(value);
	};

	const onMouseOverDescriptionPopup = (e: React.MouseEvent) => {
		e.stopPropagation();
	};

	const onViewDescriptionSave = async (e: React.MouseEvent) => {
		e.stopPropagation();

		const {user, app} = appState;

		if (user.isAdmin || editedViewMaybe?.ownedBy === user.id) {
			if (descriptionValue !== editedViewMaybe.description) {
				editedViewMaybe.description = descriptionValue;
				setIsDescriptionSaving(true);
				await app.transport.services.view.update(editedViewMaybe.getData());
				setIsDescriptionSaving(false);
			}

			setEditedViewId("");
		}
	};

	const onDescriptionTooltipClose = (e: React.MouseEvent) => {
		clearTimeout(_timeOutId.current);
		setHoveredDescriptionId("");

		e.stopPropagation();
	};

	const onDescriptionTooltipOpen = (hoveredId: string) => {
		if (hoveredDescriptionId !== hoveredId) {
			clearTimeout(_timeOutId.current);

			_timeOutId.current = window.setTimeout(() => {
				setHoveredDescriptionId(hoveredId);
			}, 200);
		}
	};

	const getOptions = (view: View): (IDropdownOption | ISubOptionWrapper)[] => {
		const user = appState.user;
		const sharedWithMe = view.ownedBy !== user?.id && !view.isGlobal;
		const sharedWithMeWithEdit = sharedWithMe && view?.getPermission(user?.id) > 1;
		const isGlobal = view.isGlobal;
		const userIsOwner = view?.ownedBy === user?.id && !view.isGlobal;
		const options: (IDropdownOption | ISubOptionWrapper)[] = [];

		const onShareClick = () => {
			setViewItemForSharePanel(view);
			onClose();
		};

		if ((isGlobal && (user.isAdmin || sharedWithMeWithEdit)) || userIsOwner || sharedWithMeWithEdit) {
			options.push({
				label: view.description ? "Edit Description" : "Add Description",
				onClick: () => setEditedViewId(view.id),
				IconComponent: DescriptionIcon,
			});
		}

		if (userIsOwner || sharedWithMeWithEdit) {
			options.push({
				label: "Share",
				onClick: onShareClick,
				IconComponent: ShareIcon,
			});
		}

		if (userIsOwner || sharedWithMeWithEdit || (isGlobal && user.isAdmin)) {
			options.push({
				label: "Rename",
				onClick: () => onRenameClick(view),
				IconComponent: RenameIcon,
			});
		}

		options.push({
			label: "Duplicate",
			onClick: () => onDuplicateClick(view),
			IconComponent: DuplicateIcon,
		});

		if (user.isAdmin && (userIsOwner || sharedWithMeWithEdit)) {
			options.push({
				label: "Convert to Global View",
				onClick: () => onMarkAsGlobalClick(view),
				IconComponent: GlobeIcon,
			});
		}

		if (isGlobal && user.isAdmin) {
			options.push({
				label: "Convert to Private View",
				onClick: () => onMarkAsPrivateClick(view),
				IconComponent: UserIcon,
			});
		}

		if (userIsOwner) {
			options.push({
				label: "Change Owner",
				onClick: onShareClick,
				IconComponent: SwitchIcon,
			});
		}

		if (userIsOwner || (isGlobal && user.isAdmin)) {
			options.push({
				label: "Delete",
				onClick: () => onDeleteClick(view),
				IconComponent: DeleteIcon,
			});
		}

		return options;
	};

	const onCloseNotification = useCallback(
		(key: keyof IViewNotificationStatus) => {
			setViewNotificationStatus(key, false);
		},
		[setViewNotificationStatus],
	);

	const onRenameApply = async (value: string, view: View) => {
		const isNameValid = actions.isNameValidForViewOrReport(value, feature, view.id);

		if (isNameValid) {
			if (value !== view.name) {
				view.name = value;
				setViewInNameEditMode(null);
				await appState.app.transport.services.view.update(view.getData());
				onClose();
			}
		}
	};

	const onNewViewClick = useCallback(
		async (event?: {stopPropagation: () => void}) => {
			event?.stopPropagation();

			const baseName = "New View";
			const view = View.createNew(feature, baseName, appState);

			let counter = 1;

			while (!actions.isNameValidForViewOrReport(view.name, feature, view.id)) {
				view.name = `${baseName} (${counter++})`;
			}

			const {result: viewData} = await viewService.create(view.getData(), feature);

			// Select the newly created view
			const newView = actions.getViewById(viewData.viewID);

			onChangeView(newView);
			setViewInNameEditMode(newView);

			requestAnimationFrame(() => {
				const objDiv = document.getElementById("DropdownOptionsV5");

				if (objDiv) {
					objDiv.scrollTop = objDiv.scrollHeight;
				}
			});
		},
		[appState, actions, feature, viewService, onChangeView],
	);

	const openSelectView = useCallback(() => {
		setIsOpenSelectView(true);
		onNewViewClick();
	}, [onNewViewClick]);

	const selectedView = actions.getSelectedView(feature);

	const onKeyDown = (event: React.KeyboardEvent, value: string | View) => {
		switch (event.key) {
			case KeyboardListener.KEY_ENTER:
				actions.selectView(value as View, ![XyiconFeature.Portfolio, XyiconFeature.XyiconCatalog].includes(feature));

				if (viewInNameEditMode) {
					setViewInNameEditMode(null);
					onClose();
				}
				break;
		}
	};

	useEffect(() => {
		const hoveredViewElementMaybe = viewRefs.current[hoveredDescriptionId];

		if (hoveredViewElementMaybe && tooltipRef.current) {
			setDescriptionTooltipTransform(
				DomUtils.getFixedFloatingElementPosition(
					hoveredViewElementMaybe,
					tooltipRef.current,
					VerticalAlignment.center,
					HorizontalAlignment.outerRight,
					0,
					65,
				),
			);
		}
	}, [hoveredDescriptionId]);

	useEffect(() => {
		setDescriptionValue(editedViewMaybe?.description ?? "");
	}, [editedViewMaybe]);

	useEffect(() => {
		const noSpacesParams: INotificationProps = {
			type: NotificationType.Warning,
			title: "No Spaces Created",
			lifeTime: Infinity,
			description: "This view is currently unavailable because there are no spaces to load. If you want to access this view, create a new space.",
		};

		if (shouldShowNoSpacesNotification) {
			const spaces = actions.getList<Space>(XyiconFeature.Space);

			if (spaces.length === 0) {
				notify(notificationContainer, noSpacesParams);
			}
		}
	}, [notificationContainer, actions, shouldShowNoSpacesNotification, onCloseNotification]);

	useEffect(() => {
		const extendedparams: INotificationProps = {
			type: NotificationType.Message,
			title: `You don't have any views`,
			lifeTime: Infinity,
			description: "You can request views to be shared with you directly from an admin, or create one yourself",
			buttonLabel: "Create",
			onActionButtonClick: isAnyTypeNotHidden && openSelectView,
			onCloseViewNotification: () => onCloseNotification(feature as keyof IViewNotificationStatus),
		};

		let notification: INotificationProps | undefined;

		if (views.length === 0) {
			if (viewNotificationStatus[feature as keyof IViewNotificationStatus]) {
				notification = notify(notificationContainer, extendedparams);
			}
		}

		return () => {
			if (notification) {
				notification.onClose();
			}
		};
	}, [views, notificationContainer, viewNotificationStatus, feature, isAnyTypeNotHidden, openSelectView, onCloseNotification]);

	const renderView = (view: View) => {
		const user = appState.user;
		const userIsOwner = view?.ownedBy === user.id;
		const IconComponent = view?.isGlobal ? GlobeIcon : userIsOwner ? UserIcon : UsersIcon;
		const typeName = view?.isGlobal ? "Global" : userIsOwner ? "My View" : "Shared With Me";

		const inlineStyleForTooltip: React.CSSProperties = viewRefs.current[view.id] && {
			transform: descriptionTooltipTransform?.translate,
		};

		if (view === viewInNameEditMode) {
			const getErrorMessage = (viewName: string) => {
				let errorMessage: string = "";

				if (!viewName.trim()) {
					errorMessage = "View name cannot be empty!";
				} else if (!actions.isNameValidForViewOrReport(viewName, feature, view.id)) {
					errorMessage = "View name needs to be unique!";
				}

				return errorMessage;
			};

			return (
				<ViewRowStyled
					key={view.id}
					ref={viewInNameEditModeRef}
					style={{paddingRight: "0"}}
					data-cy={`view-name-input-${view.name}`}
				>
					<IconComponent />
					<TextInputV5
						style={{color: colorPalette.primary.c500Primary, borderColor: colorPalette.primary.c500Primary}}
						value={view.name}
						onChange={(value) => {
							onRenameApply(value, view);
						}}
						getErrorMessage={getErrorMessage}
						autoFocus={true}
						onKeyDown={(e) => onKeyDown(e, view)}
					/>
				</ViewRowStyled>
			);
		}

		return (
			<ViewRowStyled
				key={view.id}
				data-cy={`view-row-${view.name}`}
				onClick={() => {
					actions.selectView(view);
					onClose();
				}}
				className={ReactUtils.cls({isActive: selectedView === view})}
				onMouseOver={() => onDescriptionTooltipOpen(view.id)}
				onMouseLeave={onDescriptionTooltipClose}
			>
				<div className="title">
					<IconComponent />
					<div
						className="viewName"
						ref={(el) => {
							viewRefs.current[view.id] = el;
						}}
					>
						{view.name}
					</div>
				</div>
				<DropdownButtonV5
					button={<DotsHorizontalIcon />}
					options={getOptions(view)}
					optionsZIndex={zIndex.contextOptions + 1}
					onHover={onDescriptionTooltipClose}
				/>
				{hoveredDescriptionId === view.id && view.description && inlineStyleForTooltip && (
					<DomPortal destination={appState.app.modalContainer}>
						<ViewTooltipStyled
							ref={tooltipRef}
							style={inlineStyleForTooltip}
						>
							<h4>{view.name}</h4>
							<p style={{whiteSpace: "pre-line"}}>{view.description}</p>
							<FlexCenter $gap="4px">
								<IconComponent />
								<span>{typeName}</span>
							</FlexCenter>
							{!view.isGlobal && !userIsOwner && (
								<FlexCenter>
									<span>Owner:&nbsp;</span>
									{actions.findUser(view.ownedBy).fullName}
								</FlexCenter>
							)}
						</ViewTooltipStyled>
					</DomPortal>
				)}
				{editedViewId === view.id && (
					<PopupV5
						onClose={() => setEditedViewId("")}
						label="View Description"
						width="400px"
						parentRef={viewRefs.current[view.id]}
						horizontalAlignment={HorizontalAlignment.outerRight}
						verticalAlignment={VerticalAlignment.bottomOuter}
						offsetX={-170}
						offsetY={8}
						buttonProps={{
							label: isDescriptionSaving ? "Saving..." : "Save",
							onClick: onViewDescriptionSave,
							hideIcon: true,
							disabled: isDescriptionSaving || descriptionValue === view.description,
						}}
						style={{zIndex: zIndex.viewDescriptionPopup, visibility: "visible"}}
						className="edit-view-description-popup"
						isSmallPopup={true}
						onMouseOver={onMouseOverDescriptionPopup}
						arrowIndicator="top-center"
					>
						<TextInputV5
							value={descriptionValue}
							onInput={onEditDescriptionInput}
							isTextArea={true}
							autoFocus={true}
							style={{height: 100}}
							placeholder="Add Description"
						/>
					</PopupV5>
				)}
			</ViewRowStyled>
		);
	};

	return (
		<Observer>
			{() => {
				const selectedView = actions.getSelectedView(feature);
				const filteredViews = views.filter((v: View) => StringUtils.containsIgnoreCase(v.name, search));

				return (
					<>
						<ViewSelectorStyled data-cy={`${XyiconFeature[feature].toLowerCase()}-view-selector`}>
							<ViewRowStyled
								ref={selectedViewRef}
								onClick={() => setIsOpenSelectView((o) => !o)}
								onMouseLeave={() => isOpenSelectView && selectedViewRef?.current.classList.add("hover")}
							>
								<div>{selectedView?.name ?? ""}</div>
								<ChevronDownIcon />
							</ViewRowStyled>
							{isOpenSelectView && (
								<ViewListPopupStyled ref={viewPopupRef}>
									<ViewListPopupHeaderStyled>
										<SearchFieldV5
											value={search}
											onInput={setSearch}
											cyId={`${XyiconFeature[feature].toLowerCase()}-view-selector-search-field`}
										/>
										<IconButtonV5
											title="Create new View"
											IconComponent={CirclePlusIcon}
											onClick={onNewViewClick}
										/>
									</ViewListPopupHeaderStyled>
									<ViewListStyled data-cy={`${XyiconFeature[feature].toLowerCase()}-view-list`}>
										{filteredViews.length === 0 && <NoViewsStyled data-cy="no-views-to-display">No views to display</NoViewsStyled>}
										{filteredViews.map((v) => renderView(v))}
									</ViewListStyled>
								</ViewListPopupStyled>
							)}
						</ViewSelectorStyled>
						{viewItemForSharePanel && (
							<ViewSharingPopup
								view={viewItemForSharePanel}
								onClose={onCloseSharePanel}
							/>
						)}
					</>
				);
			}}
		</Observer>
	);
};

const NoViewsStyled = styled.div`
	${FLEXCENTER};
	height: 24px;
`;

const ViewListPopupHeaderStyled = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: ${baseDistance.sm};
	gap: ${baseDistance.sm};
	align-items: center;

	${SearchFieldStyled} {
		width: 100%;
	}
`;

const ViewListPopupStyled = styled.div`
	width: 280px;
	height: 400px;
	position: absolute;
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.xs};
	margin-top: ${baseDistance.sm};
	background: ${colorPalette.white};
	border-radius: ${radius.sm};
	z-index: 4;
	padding: ${baseDistance.sm};
	box-shadow: 0px 4px 8px 0px #00000033;
`;

const ViewRowStyled = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	width: 100%;
	height: 32px;
	gap: ${baseDistance.xs};
	cursor: pointer;
	padding: ${baseDistance.sm};
	border-radius: ${radius.sm};

	&:hover,
	&.hover {
		background-color: ${colorPalette.gray.c200Light};
	}

	&.isActive {
		background-color: ${colorPalette.primary.c200Light};
		color: ${colorPalette.primary.c500Primary};
	}

	.title {
		max-width: 280px;
		display: flex;
		align-items: center;
		gap: ${baseDistance.sm};
		line-height: 20px;

		> svg {
			min-width: 24px;
		}

		.viewName {
			${ELLIPSIS};
		}
	}

	input {
		border: 1px solid ${colorPalette.primary.c500Primary};
		color: ${colorPalette.primary.c500Primary};
	}

	${DropdownButtonStyled} {
		margin-left: auto;
	}
`;

const ViewListStyled = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.xs};
	max-height: 400px;
	overflow-y: auto;

	${ViewRowStyled} {
		height: 40px;
		font-size: ${fontSize.lg};
		line-height: 24px;

		.viewName {
			width: 170px;
		}
	}

	${DropdownButtonStyled} {
		width: 32px;
		height: 32px;
	}
`;

const ViewSelectorStyled = styled.div`
	font-size: ${fontSize.md};

	> ${ViewRowStyled} {
		svg {
			margin-left: ${baseDistance.xs};
			width: 18px;
			height: 18px;
		}
	}
`;
